<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div class="space-y-6 transition-all duration-300 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">

      <!-- Encabezado -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Directorio de Comercios & Clientes</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Administra restaurantes, farmacias, tiendas B2B y clientes particulares B2C.</p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-[#F36421] hover:bg-[#d95214] text-white font-bold rounded-xl shadow-lg shadow-[#F36421]/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nuevo Comercio / Cliente
        </button>
      </div>

      <!-- Filtros y Búsqueda -->
      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por marca, responsable, RUC, cédula o WhatsApp..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all"
          >
        </div>
        <select v-model="typeFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Todos los Perfiles</option>
          <option value="B2B_BUSINESS">Comercios / Restaurantes (B2B)</option>
          <option value="B2C_PARTICULAR">Particulares (B2C)</option>
        </select>
        <select v-model="statusFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Todos los Estados</option>
          <option value="ACTIVE">Activo</option>
          <option value="PENDING">Pendiente</option>
          <option value="BLOCKED">Bloqueado</option>
        </select>
      </div>

      <!-- Tabla Operativa de Clientes -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Tipo / Perfil</th>
                <th class="px-6 py-4 font-semibold">Comercio / Responsable</th>
                <th class="px-6 py-4 font-semibold">Despacho & WhatsApp</th>
                <th class="px-6 py-4 font-semibold">Sucursales / Libreta</th>
                <th class="px-6 py-4 font-semibold">Comisión & Crédito</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">Cargando directorio de clientes LLEVO...</td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">No se encontraron clientes que coincidan.</td>
              </tr>

              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">

                <!-- Tipo (Badge B2B vs B2C) -->
                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-extrabold"
                    :class="item.customer_type === 'B2B_BUSINESS' ? 'bg-orange-100 text-[#F36421]' : 'bg-blue-100 text-blue-700'">
                    {{ item.customer_type === 'B2B_BUSINESS' ? '🏢 B2B COMERCIO' : '👤 B2C PARTICULAR' }}
                  </span>
                </td>

                <!-- Comercio / Persona -->
                <td class="px-6 py-4">
                  <div>
                    <div class="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                      {{ item.business_name || `${item.first_name} ${item.last_name}` }}
                    </div>
                    <div class="text-xs text-slate-500 mt-0.5">
                      {{ item.business_name ? `Resp: ${item.first_name} ${item.last_name}` : `Código: ${item.code || 'N/D'}` }}
                    </div>
                    <div class="text-xs font-mono text-slate-400 mt-0.5">
                      RUC/CÉD: {{ item.identity_document || '--' }}
                    </div>
                  </div>
                </td>

                <!-- WhatsApp -->
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-800 dark:text-slate-200">📱 {{ item.mobile_phone || '--' }}</div>
                  <div class="text-xs text-slate-500 truncate max-w-[160px]">{{ item.email || 'Sin correo' }}</div>
                </td>

                <!-- Sucursales Guardadas -->
                <td class="px-6 py-4">
                  <div class="flex items-center gap-1.5">
                    <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-[#142032] text-white">
                      {{ (item.addresses && item.addresses.length) || 0 }} Sucursales
                    </span>
                  </div>
                  <div v-if="item.addresses && item.addresses.length > 0" class="text-xs text-slate-400 truncate max-w-[180px] mt-1">
                    ★ {{ item.addresses.find(a => a.is_default)?.alias || item.addresses[0].alias }}: {{ item.addresses.find(a => a.is_default)?.address || item.addresses[0].address }}
                  </div>
                  <div v-else class="text-xs text-red-500 mt-1">Sin dirección principal</div>
                </td>

                <!-- Crédito & Comisiones LLEVO -->
                <td class="px-6 py-4 font-mono text-xs">
                  <div class="font-bold text-emerald-600">{{ item.commission_rate || '0.00' }}% Comisión</div>
                  <div class="text-slate-500">Crédito: C$ {{ formatMoney(item.credit_limit) }}</div>
                </td>

                <!-- Estado -->
                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="getStatusClasses(item.status)">
                    <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDot(item.status)"></span>
                    {{ getStatusLabel(item.status) }}
                  </span>
                </td>

                <!-- Acciones (Sin balances crediticios) -->
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button @click="openEdit(item)" class="p-2 text-slate-400 hover:text-[#F36421] hover:bg-orange-50 rounded-lg transition-colors" title="Editar Perfil y Direcciones">
                      <FileText class="w-5 h-5" />
                    </button>
                    <button @click="deleteItem(item)" class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar Cliente">
                      <Trash2 class="w-5 h-5" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} clientes registrados</span>
        </div>
      </div>
    </div>

    <!-- Formulario Modal -->
    <Form
      :show="showModal"
      :applicationToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, FileText, Trash2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import Form from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const typeFilter = ref('');
const showModal = ref(false);
const selectedItem = ref(null);

const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/customers');
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error cargando el directorio de clientes');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value) result = result.filter(i => i.status === statusFilter.value);
  if (typeFilter.value) result = result.filter(i => i.customer_type === typeFilter.value);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => {
      const bName = (i.business_name || '').toLowerCase();
      const name = `${i.first_name || ''} ${i.last_name || ''}`.toLowerCase();
      const doc = (i.identity_document || '').toLowerCase();
      const phone = (i.mobile_phone || '').toLowerCase();
      return bName.includes(term) || name.includes(term) || doc.includes(term) || phone.includes(term);
    });
  }
  return result;
});

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const getStatusClasses = (status) => {
  const map = {
    'PENDING': 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
    'ACTIVE': 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400',
    'BLOCKED': 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-400',
  };
  return map[status] || map['PENDING'];
};

const getStatusDot = (status) => {
  const map = { 'PENDING': 'bg-slate-400', 'ACTIVE': 'bg-emerald-500', 'BLOCKED': 'bg-red-500' };
  return map[status] || 'bg-slate-400';
};

const getStatusLabel = (status) => {
  const map = { 'PENDING': 'Pendiente', 'ACTIVE': 'Activo', 'BLOCKED': 'Bloqueado' };
  return map[status] || 'Pendiente';
};

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar Cliente?',
    text: "Se borrarán también sus sucursales y libreta de direcciones.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/customers/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Cliente eliminado del directorio.');
      } catch (e) {
        showError('No se pudo eliminar al cliente.');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
</style>